import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { motion } from 'framer-motion';
import { db } from '../FirebaseConfig';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import noImg from "../assets/ChatGPT Image May 4, 2025, 05_02_10 AM.png"

const PropertyPage = () => {
      const canvasRef = useRef(null);
    const [properties, setProperties] = useState([]);
    const [activeCategory, setActiveCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const propertyPerPage = 8;
      useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    const renderer = new THREE.WebGLRenderer({
      canvas: canvas,
      alpha: true,
      antialias: true,
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x000000, 0);

    const geometry = new THREE.BoxGeometry(1.5, 1.5, 1.5);
    const material = new THREE.MeshStandardMaterial({
      color: 0x93c5fd,
      roughness: 0.5,
      metalness: 0.8,
      transparent: true,
      opacity: 0.7,
    });
    const cube = new THREE.Mesh(geometry, material);
    scene.add(cube);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);
    const pointLight = new THREE.PointLight(0x3b82f6, 1, 100);
    pointLight.position.set(5, 5, 5);
    scene.add(pointLight);

    camera.position.z = 5;

    const animate = () => {
      requestAnimationFrame(animate);
      cube.rotation.x += 0.005;
      cube.rotation.y += 0.005;
      renderer.render(scene, camera);
    };

    animate();

    const onWindowResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', onWindowResize);

    return () => {
      window.removeEventListener('resize', onWindowResize);
      renderer.dispose();
    };
  }, []);

    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true);

            try {
                const q = query(
                    collection(db, 'listings'),
                    orderBy('createdAt', 'desc')
                );

                const snapshot = await getDocs(q);
                const allProperty = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                // Instead of slicing here, we'll slice the filtered products
                setProperties(allProperty);
            } catch (err) {
                console.error('Error fetching products:', err);
            }

            setLoading(false);
        };

        fetchProducts();
    }, []);

    const handlePagination = (direction) => {
        setPage(prevPage => prevPage + direction);
    };

    const truncateText = (text, maxLength) => {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    const filteredAndSearchedProperties = properties.filter(product => {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch =
            product.name?.toLowerCase().includes(searchLower) ||
            product.description?.toLowerCase().includes(searchLower);

        const matchesLocation =
            activeCategory === 'all' ||
            product.location?.toLowerCase().includes(activeCategory.toLowerCase());

        return matchesSearch && matchesLocation;
    });

    // Apply pagination after filtering
  const start = (page - 1) * propertyPerPage;
  const end = start + propertyPerPage;
  const paginatedProducts = filteredAndSearchedProperties.slice(start, end);
  const totalPages = Math.ceil(filteredAndSearchedProperties.length / propertyPerPage);

    return (
    <div className="relative min-h-screen font-sans text-gold bg-black/50">
      {/* 3D Animation Canvas */}
      <canvas ref={canvasRef} className="fixed inset-0 z-[-1] opacity-40 pointer-events-none" />

      <div className="relative z-10 p-6 sm:p-10">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-center mb-6">
          Find Your Perfect Property
        </h1>

        {/* Categories */}
        <div className="flex flex-wrap justify-center gap-3 mb-6">
          {['all', 'hilltop', 'Odim', 'Odenigwe', 'Behind Flat'].map((category) => (
            <motion.button
              key={category}
              className={`px-4 py-2 rounded-full font-medium backdrop-blur-md transition ${
                activeCategory === category ? 'bg-white/30 text-gold-500' : 'bg-white/10 text-gray-200 hover:bg-white/20'
              }`}
              onClick={() => {
                setActiveCategory(category);
                setPage(1);
              }}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </motion.button>
          ))}
        </div>

        {/* Search Bar */}
        <div className="relative mb-8 max-w-xl mx-auto">
          <input
            type="text"
            placeholder="Search properties..."
            className="w-full p-3 rounded-2xl bg-white/10 text-white pl-10 outline-none border border-white/20 backdrop-blur-md placeholder-gray-300"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="absolute top-3 left-3 text-gray-300"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
        </div>

        {/* Property Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 m-auto max-w-7xl">
          {loading ? (
            <div className="flex justify-center items-center col-span-full h-64">
              <div className="animate-spin rounded-full border-t-2 border-blue-400 w-12 h-12"></div>
            </div>
          ) : paginatedProducts.length > 0 ? (
            paginatedProducts.map((product) => (
              <motion.div
                key={product.id}
                className="bg-white/10 backdrop-blur-lg text-white rounded-3xl p-6 shadow-2xl flex flex-col hover:bg-white/20 transition-colors"
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-full h-48 overflow-hidden rounded-2xl mb-4 shadow-inner">
                  <img
                    src={product.images && product.images[0] ? product.images[0] : noImg}
                    loading="lazy"
                    alt={product.name || 'Property image'}
                    className="object-cover w-full h-full"
                  />
                </div>
                <h3 className="text-xl font-bold mb-1">{truncateText(product.name, 25)}</h3>
                <p className="text-sm text-gray-300 mb-2">
                  Location: {product.location || 'Not specified'}
                </p>
                <p className="text-xl font-semibold mb-3">
                  {product.price ? `₦${Number(product.price).toLocaleString()}` : 'Price not available'}
                </p>
                <Link
                  to={`/listing/${product.id}`}
                  className="mt-auto text-center bg-transaparent border-2 border-gold hover:bg-yellow-700 px-6 py-3 rounded-full text-white font-semibold transition-colors"
                >
                  View Details
                </Link>
              </motion.div>
            ))
          ) : (
            <div className="text-center text-gray-300 col-span-full py-20">
              No properties found matching your criteria.
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        <div className="flex justify-center mt-10 space-x-4">
          <button
            className="bg-white/20 px-6 py-2 rounded-full text-gray-200 font-semibold disabled:opacity-50 transition-colors hover:bg-white/30"
            onClick={() => handlePagination(-1)}
            disabled={page === 1 || loading}
          >
            Previous
          </button>
          <button
            className="bg-white/20 px-6 py-2 rounded-full text-gray-200 font-semibold disabled:opacity-50 transition-colors hover:bg-white/30"
            onClick={() => handlePagination(1)}
            disabled={loading || page >= totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropertyPage;